import React from 'react'
import { Dropdown, DropdownButton, Form, ListGroup, Stack } from 'react-bootstrap'
import map from 'lodash/map'

import type { DateType } from '@ztechapps/common.utils'
import { DateIsEnum, DateNameEnum, formatShortDate } from '@ztechapps/common.utils'
import { DateTimeInput } from '@ztechapps/webui.date'

const displayedDateName = [
  DateNameEnum.Today,
  DateNameEnum.ThisWeek,
  DateNameEnum.ThisMonth,
  DateNameEnum.ThisYear,
  DateNameEnum.Yesterday,
  DateNameEnum.LastWeek,
  DateNameEnum.LastMonth,
  DateNameEnum.LastYear,
  DateNameEnum.Tomorrow,
  DateNameEnum.NextWeek,
  DateNameEnum.NextMonth,
  DateNameEnum.NextYear,
]

/**
 * Props for the DateFilter component.
 * Supports both preset date filters (Today, This Week, etc.) and custom date ranges.
 */
type DateFilterProps = {
  /** Function to determine if a preset filter is currently active */
  isActive: (current: DateNameEnum) => boolean
  /** Callback when a preset filter is clicked */
  onClickListItem: (dateNameArg: DateNameEnum) => void
  /** Start date for custom range filter */
  startDate?: DateType
  /** End date for custom range filter */
  endDate?: DateType
  /** Callback when start date changes */
  onStartDateChange?: (date: DateType) => void
  /** Callback when end date changes */
  onEndDateChange?: (date: DateType) => void
  /** Callback for keyboard events on date inputs (e.g., Enter key to apply filter) */
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  /** Whether to show the custom date range inputs. Defaults to true. */
  showCustomRange?: boolean
}

/**
 * DateFilter component provides a UI for selecting date ranges.
 * Includes preset options (Today, This Week, etc.) and optional custom date range inputs.
 *
 * @param props - DateFilterProps
 * @returns React component displaying date filter options
 */
export const DateFilter = ({ isActive, onClickListItem, startDate, endDate, onStartDateChange, onEndDateChange, onKeyPress, showCustomRange = true }: DateFilterProps) => {

  return (
    <Stack gap={3}>
      <ListGroup variant="flush">
        {map(displayedDateName, (e) => (
          <ListGroup.Item
            key={e}
            action
            active={isActive(e)}
            onClick={() => {
              onClickListItem(e)
            }}
          >
            {e}
          </ListGroup.Item>
        ))}

        {showCustomRange && (
          <ListGroup.Item>
            <Stack gap={2}>
              <div>
                <strong>Custom Range</strong>
              </div>

              <Stack gap={2}>
                <Form.Group>
                  <Form.Label htmlFor="date-filter-start">Start Date</Form.Label>
                  <DateTimeInput id="date-filter-start" type="date" value={startDate} onChange={onStartDateChange}
                                 onKeyPress={onKeyPress} />
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor="date-filter-end">End Date</Form.Label>
                  <DateTimeInput id="date-filter-end" type="date" value={endDate} onChange={onEndDateChange}
                                 onKeyPress={onKeyPress} />
                </Form.Group>
              </Stack>
            </Stack>
          </ListGroup.Item>
        )}

      </ListGroup>



    </Stack>
  )
}

export const DateIsDropdown = ({ value, setValue }: { value: DateIsEnum; setValue: (value: DateIsEnum) => void }) => {
  return (
    <DropdownButton title={value} variant="primary">
      {map(DateIsEnum, (m) => (
        <Dropdown.Item onClick={() => setValue(m)} key={m}>
          {m}
        </Dropdown.Item>
      ))}
    </DropdownButton>
  )
}

export const DateIsValueDropdown = ({ value, setValue }: { value: DateNameEnum; setValue: (value: DateNameEnum) => void }) => {
  if (value === DateNameEnum.ExactDate) {
    return <DateTimeInput type="date" value={formatShortDate(value)} onChange={setValue} />
  }

  //show the date dropdown only if the value is not an exact date
  return (
    <DropdownButton title={value} variant="primary">
      {map(displayedDateName, (m) => (
        <Dropdown.Item onClick={() => setValue(m)} key={m}>
          {m}
        </Dropdown.Item>
      ))}

      <Dropdown.Item onClick={() => setValue(DateNameEnum.ExactDate)}>{DateNameEnum.ExactDate}</Dropdown.Item>
    </DropdownButton>
  )
}
